import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Podcasts as PodcastsIcon, AccountCircle as AccountCircleIcon } from '@mui/icons-material';
import userStore from '../Stores/User';
import { isPaidProUser } from '../constants/plans';
import gamificationStore from '../Stores/GamificationStore';
import BonusIndicator from './BonusIndicator';

const TopNavBar = observer(() => {
  const location = useLocation();
  const [isNarrowScreen, setIsNarrowScreen] = useState(false);
  
  // Check screen width to determine if we should hide the podcast button
  useEffect(() => {
    const checkScreenWidth = () => {
      setIsNarrowScreen(window.innerWidth < 400);
    };

    checkScreenWidth();
    window.addEventListener('resize', checkScreenWidth);
    return () => window.removeEventListener('resize', checkScreenWidth);
  }, []);
  
  // Hide the top nav bar on certain pages where it might conflict
  if (location.pathname.startsWith('/onboarding/') || 
      location.pathname.startsWith('/share/') || 
      location.pathname === '/feeds' || 
      location.pathname.startsWith('/feed/')) {
    return null;
  }

  const logo = process.env.PUBLIC_URL + '/logo64.png';

  // Check if user is pro
  const isProUser = userStore.user?.subscriptions?.raw_subscriptions?.[0]?.plan?.id 
    ? isPaidProUser(userStore.user)
    : false;

  const handleProfileClick = () => {
    window.location.href = '/settings';
  };

  const handlePodcastClick = () => {
    window.location.href = '/setupnew';
  };

  const handleUpgradeClick = async () => {
    const hasStripeCustomerId = userStore.user?.stripe_customer_id || userStore.user?.customer_id;
    const hasActiveSubscription = userStore.user?.subscriptions?.raw_subscriptions?.[0]?.plan?.id;
    if (hasStripeCustomerId && !hasActiveSubscription) {
      try {
        const response = await import('../audiblogsApi').then(m => m.createReactivationCheckoutSession(userStore.idToken, {}, '/add'));
        if (response.data.url) {
          window.location.href = response.data.url;
        } else {
          window.location.href = '/onboarding/choosevoice';
        }
      } catch {
        window.location.href = '/onboarding/choosevoice';
      }
    } else {
      window.location.href = '/onboarding/choosevoice';
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '80px',
        backgroundColor: '#000000',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 20px',
        zIndex: 1000,
        backdropFilter: 'blur(10px)',
        boxShadow: '0 2px 20px rgba(0, 0, 0, 0.3)',
        marginBottom: '20px'
      }}
    >
      {/* Logo on the left */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer'
        }}
        onClick={() => window.location.href = '/'}
      >
        <img 
          src={logo} 
          alt="AudioRead" 
          style={{
            height: '40px',
            width: 'auto'
          }}
        />
        <span
          style={{
            marginLeft: '2px',
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#ffffff',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}
        >
          Audioread
        </span>
      </div>

      {/* Icons on the right */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        gap: '16px',
        justifyContent: 'flex-end'
      }}>
        {/* Upgrade button for non-pro users */}
        {!isProUser && userStore.user && (
          <div
            onClick={handleUpgradeClick}
            style={{
              height: '36px',
              padding: '0 18px',
              borderRadius: '18px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '13px',
              color: '#fff',
              fontWeight: '600',
              letterSpacing: '0.5px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3), 0 2px 8px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                          whiteSpace: 'nowrap',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            flexShrink: 0
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px) scale(1.02)';
              e.target.style.boxShadow = '0 8px 30px rgba(102, 126, 234, 0.4), 0 4px 12px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
              e.target.style.background = 'linear-gradient(135deg, #7c8ff0 0%, #8a5bb8 50%, #f5a0ff 100%)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0) scale(1)';
              e.target.style.boxShadow = '0 4px 20px rgba(102, 126, 234, 0.3), 0 2px 8px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
              e.target.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)';
            }}
            onMouseDown={(e) => {
              e.target.style.transform = 'translateY(1px) scale(0.98)';
            }}
            onMouseUp={(e) => {
              e.target.style.transform = 'translateY(-1px) scale(1.02)';
            }}
            title="Upgrade to Pro"
          >
            <span style={{ 
              marginRight: '4px', 
              fontSize: '12px',
              filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))'
            }}>⭐</span>
            Upgrade
          </div>
        )}

        {/* Podcast icon - show for all users, but hide on mobile when upgrade button is shown */}
        {userStore.user && (!isNarrowScreen || isProUser) && (
          <BonusIndicator action={gamificationStore.actions.PODCAST_SETUP}>
            <div
              onClick={handlePodcastClick}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: 'rgba(24, 15, 34, 0.8)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '18px',
                color: '#fff',
                fontWeight: 'bold',
                transition: 'all 0.2s ease',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(59, 130, 246, 0.9)';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'rgba(24, 15, 34, 0.8)';
                e.target.style.transform = 'scale(1)';
              }}
              title="Podcast Setup"
            >
              <PodcastsIcon style={{ 
                fontSize: '20px', 
                backgroundColor: 'transparent', 
                display: 'block',
                margin: 0,
                padding: 0,
                lineHeight: 1
              }} />
            </div>
          </BonusIndicator>
        )}

        {/* Profile icon - hide on settings page since we're already there */}
        {location.pathname !== '/settings' && (
          <div
            onClick={handleProfileClick}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: 'rgba(24, 15, 34, 0.8)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '18px',
              color: '#fff',
              fontWeight: 'bold',
              transition: 'all 0.2s ease',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(59, 130, 246, 0.8)';
              e.target.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'rgba(24, 15, 34, 0.8)';
              e.target.style.transform = 'scale(1)';
            }}
            title="Settings"
          >
            <AccountCircleIcon style={{ fontSize: '20px', color: '#fff' }} />
          </div>
        )}
      </div>
    </div>
  );
});

export default TopNavBar; 